import { getUserPrefs, setUserPrefs } from './cookieUtils';
import { logInfo, logError } from './logUtils';

// Local storage key as fallback if cookies are disabled
const LS_WELCOME_COMPLETED_KEY = 'welcome_completed';

/**
 * Check if the user is a first-time visitor
 * @returns boolean indicating if the user is visiting for the first time
 */
export function isFirstTimeUser(): boolean {
  try {
    // First try to get from cookies (preferred method)
    const userPrefs = getUserPrefs() as any;
    
    // Add debugging to trace the issue
    logInfo('Checking first-time user status:', { 
      userPrefs, 
      hasPrefs: !!userPrefs,
      hasCompletedWelcome: userPrefs?.hasCompletedWelcome
    });
    
    if (userPrefs && userPrefs.hasCompletedWelcome === true) {
      logInfo('User has completed welcome (from cookies)');
      return false;
    }
    
    // Fallback to localStorage if cookies fail or aren't available
    try {
      const localStorageValue = localStorage.getItem(LS_WELCOME_COMPLETED_KEY);
      logInfo('LocalStorage welcome status:', localStorageValue);
      
      if (localStorageValue === 'true') {
        // Sync the value to cookies for next time
        markWelcomeCompleted();
        logInfo('User has completed welcome (from localStorage)');
        return false;
      }
    } catch (error) {
      logError('Error accessing localStorage:', error);
    }
    
    // If we have no record in either place, user is a first-time visitor
    logInfo('User is a first-time visitor');
    return true;
  } catch (error) {
    logError('Error checking first-time user status:', error);
    // Default to showing welcome if there's an error - safer than blocking users
    return true;
  }
}

/**
 * Mark the user as having completed the welcome flow
 */
export function markWelcomeCompleted(): void {
  try {
    // Get existing preferences or create an empty object
    const existingPrefs = getUserPrefs() as object || {};
    
    // Update with welcome completion info
    setUserPrefs({
      ...existingPrefs,
      hasCompletedWelcome: true,
      welcomeCompletedAt: new Date().toISOString(),
      isFirstTimeUser: false
    }, 365); // Store for a year
    
    // Also store in localStorage as a fallback
    try {
      localStorage.setItem(LS_WELCOME_COMPLETED_KEY, 'true');
    } catch (error) {
      logError('Error writing to localStorage:', error);
    }
  } catch (error) {
    logError('Error marking welcome as completed:', error);
    // Attempt localStorage as fallback
    try {
      localStorage.setItem(LS_WELCOME_COMPLETED_KEY, 'true');
    } catch (innerError) {
      logError('Fallback localStorage also failed:', innerError);
    }
  }
}

/**
 * Reset the welcome status (for testing)
 */
export function resetWelcomeStatus(): void {
  try {
    // Reset in cookies
    const existingPrefs = getUserPrefs() as object || {};
    
    setUserPrefs({
      ...existingPrefs,
      hasCompletedWelcome: false,
      isFirstTimeUser: true,
      welcomeResetAt: new Date().toISOString()
    }, 365);
    
    // Reset in localStorage
    try {
      localStorage.removeItem(LS_WELCOME_COMPLETED_KEY);
    } catch (error) {
      logError('Error removing from localStorage:', error);
    }
    
    logInfo('Welcome status has been reset. Refresh the page to see the welcome flow.');
  } catch (error) {
    logError('Error resetting welcome status:', error);
  }
}

// Force user to be treated as first-time visitor
// Useful for testing the welcome page
export function forceFirstTimeUser(): void {
  try {
    // Clear cookie
    const existingPrefs = getUserPrefs() as object || {};
    setUserPrefs({
      ...existingPrefs,
      hasCompletedWelcome: false,
      isFirstTimeUser: true,
      welcomeForced: new Date().toISOString()
    });
    
    // Clear localStorage
    localStorage.removeItem(LS_WELCOME_COMPLETED_KEY);
    
    logInfo('User has been forced to first-time status. Refresh to see welcome page.');
  } catch (error) {
    logError('Error forcing first-time user status:', error);
  }
} 